<template>
  <div class="metrics-student-perf">
    <div class="metrics-student-perf__header">
      <div>
        <h3 class="metrics-student-perf__title heading-s">
          {{ $t("metrics.studentPerformance") }}
        </h3>
        <p class="metrics-student-perf__subtitle paragraph-s">
          {{ $t("metrics.individualScores") }}
        </p>
      </div>
      <eg-b-dropdown
        ref="dropdown"
        class="metrics-student-perf__sort"
        :text="$t('metrics.sortBy', { label: currentSortLabel })"
        right
        no-caret
        variant="link"
      >
        <eg-b-dropdown-item @click="setSort('overallScore', 'DESC')">
          {{ $t("metrics.overallScore") }}
        </eg-b-dropdown-item>
        <eg-b-dropdown-item @click="setSort('name', 'ASC')">
          {{ $t("metrics.studentName") }}
        </eg-b-dropdown-item>
      </eg-b-dropdown>
    </div>

    <div v-if="hasData" class="metrics-student-perf__table-wrap">
      <eg-pv-table
        :items="students"
        :fields="tableFields"
        @row-click="onRowClick"
      >
        <template #cell(student)="{ item }">
          <div class="metrics-student-perf__student">
            <div class="metrics-student-perf__avatar">
              {{ initials(item.name) }}
            </div>
            <span class="metrics-student-perf__name">{{ item.name }}</span>
          </div>
        </template>

        <template #cell(overallScore)="{ item }">
          <span
            class="metrics-student-perf__badge"
            :class="`metrics-student-perf__badge--${item.label}`"
          >
            {{ item.overallScore }}
          </span>
        </template>

        <template
          v-for="criterion in criteria"
          #[`cell(criterion_${criterion.id})`]="{ item }"
        >
          <span :key="criterion.id">{{ item.scores[criterion.id] }}</span>
        </template>
      </eg-pv-table>
    </div>

    <metrics-pagination
      v-if="hasData && totalPages > 1"
      :value="currentPage"
      :total-rows="total"
      :per-page="pageSize"
      @change="goToPage"
    >
      <template #label>
        {{ $t("metrics.showingStudents", { count: students.length, total }) }}
      </template>
    </metrics-pagination>

    <metrics-empty-state v-if="!hasData" />
  </div>
</template>

<script>
import MetricsEmptyState from "../shared/metrics_empty_state.vue";
import MetricsPagination from "../shared/metrics_pagination.vue";
import EgPvTable from "@/components/global/eg/eg_pv_table/eg_pv_table.vue";
import { getMetricsStudentPerformance } from "@/api/requests/metrics.js";
import dropdownHideOnLeaveMixin from "@/mixins/dropdownHideOnLeaveMixin";
import metricsViewMixin from "@/mixins/metricsViewMixin";

export default {
  name: "MetricsStudentPerformance",

  components: { MetricsEmptyState, MetricsPagination, EgPvTable },

  mixins: [dropdownHideOnLeaveMixin, metricsViewMixin],

  props: {
    assignmentId: {
      type: [String, Number],
      default: null,
    },
    performance: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      criteria: [],
      allLocalStudents: [],
      students: [],
      total: 0,
      currentPage: 1,
      pageSize: 6,
      sortBy: "overallScore",
      sortDir: "DESC",
    };
  },

  computed: {
    hasData() {
      return this.students.length > 0;
    },

    currentSortLabel() {
      return this.sortBy === "name"
        ? this.$t("metrics.studentName")
        : this.$t("metrics.overallScore");
    },

    totalPages() {
      return Math.ceil(this.total / this.pageSize);
    },

    tableFields() {
      const base = [
        {
          key: "student",
          label: this.$t("metrics.student"),
        },
        {
          key: "overallScore",
          label: this.$t("metrics.overallScore"),
          style: "min-width: 120px",
        },
      ];
      const criteriaFields = this.criteria.map((criterion) => ({
        key: `criterion_${criterion.id}`,
        label: criterion.name,
        style: "text-align: center; min-width: 120px; max-width: 200px",
      }));
      return [...base, ...criteriaFields];
    },
  },

  watch: {
    entityId: "fetchData",
    assignmentId: "fetchData",
    performance: {
      immediate: true,
      handler(val) {
        if (val) {
          this.criteria = val.criteria || [];
          this.allLocalStudents = val.data || [];
          this.total = val.total || 0;
          this.currentPage = 1;
          this.applyLocalPage();
        } else if (this.entityId) {
          this.currentPage = 1;
          this.fetchData();
        }
      },
    },
  },

  methods: {
    async fetchData() {
      if (!this.entityId || !this.assignmentId) return;
      try {
        const result = await getMetricsStudentPerformance({
          entityId: this.entityId,
          assignmentId: this.assignmentId,
          page: this.currentPage,
          limit: this.pageSize,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
        });
        const d = result?.data;
        this.criteria = d?.criteria || [];
        this.students = d?.data || [];
        this.total = d?.total ?? 0;
      } catch {
        this.criteria = [];
        this.students = [];
        this.total = 0;
      }
    },

    setSort(sortBy, sortDir) {
      this.sortBy = sortBy;
      this.sortDir = sortDir;
      this.currentPage = 1;
      this.fetchData();
    },

    goToPage(page) {
      this.currentPage = page;
      if (this.performance) {
        this.applyLocalPage();
      } else {
        this.fetchData();
      }
    },

    applyLocalPage() {
      const start = (this.currentPage - 1) * this.pageSize;
      this.students = this.allLocalStudents.slice(start, start + this.pageSize);
    },

    onRowClick(item) {
      if (!item.studentId) return;
      this.goToStudent(item.studentId, this.$route.params.classId);
    },

    initials(name) {
      return name
        .split(" ")
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../shared/metrics_card";

.metrics-student-perf {
  @include metrics-card;
  display: flex;
  flex-direction: column;
  margin: 0 24px;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  &__title {
    font-size: var(--typography-font-size-fs-400);
    font-weight: var(--typography-font-weight-fw-700, 700);
    color: var(--color-text-neutral-primary, #1a2330);
    margin: 0 0 4px;
  }

  &__subtitle {
    color: var(--color-text-neutral-fade);
    margin: 0;
  }

  &__sort {
    flex-shrink: 0;
    @include metrics-dropdown;
  }

  &__table-wrap {
    overflow-x: auto;
    margin-top: 16px;

    ::v-deep
      .p-datatable-thead
      > tr
      > th:not(:first-child)
      .p-column-header-content {
      justify-content: center;
    }

    ::v-deep .p-datatable-tbody > tr {
      cursor: pointer;

      > td:first-child {
        min-width: 240px;
      }

      > td:not(:first-child) {
        text-align: center;
      }
    }
  }

  &__student {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: var(--typography-font-weight-fw-700, 700);
    background: var(--color-surface-primary-light-1, #f6f2ff);
    color: var(--color-text-primary-default, #8a3ffc);
    flex-shrink: 0;
  }

  &__name {
    font-size: var(--typography-font-size-fs-300, 14px);
    font-weight: var(--typography-font-weight-fw-500, 500);
    color: var(--color-text-neutral-primary, #1a2330);
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    font-size: var(--typography-font-size-fs-200);
    font-weight: var(--typography-font-weight-fw-700, 700);
    padding: 0 6px;

    &--high {
      background: var(--colors-green-100, #d9f2e0);
      color: var(--color-text-semantic-onSuccess, #308c4a);
    }

    &--medium {
      background: var(--colors-yellow-200, #fffab8);
      color: var(--colors-yellow-800, #9e6c00);
    }

    &--low {
      background: var(--color-surface-semantic-error-light, #fdefec);
      color: var(--color-text-semantic-onError, #dc3545);
    }
  }
}
</style>
